import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import {
  Box,
  FormHelperText,
  InputAdornment,
  Button as MuiButton,
  Typography,
  useTheme,
} from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import type { AutocompleteOption } from "@components/Autocomplete/Autocomplete.types";
import DatePicker from "@components/DatePicker/DatePicker";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import type { CloseMovementServicePayload, Movement } from "@services/movement-services/types";
import { getMovementServiceDetailsById } from "@services/movementServicesApi";
import { getAnchorageAreas, REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import type { VesselCallResponse } from "@services/vesselCall/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { z } from "zod";
import { useCloseMovementService } from "../hooks/useCloseMovementService";
import type { MovementServiceDetailsBackendResponse } from "../preview/types";
import AddResourceDrawer from "./closeMovement/AddResourceDrawer";
import EditResourceDrawer from "./closeMovement/EditResourceDrawer";
import UpdateTimesDrawer from "./closeMovement/UpdateTimesDrawer";
import { validateMovementTimes } from "./closeMovement/validateMovementTimes";
import {
  getEarliestResourceStartTime,
  makeInputSlotProps,
  makeInputSxProps,
  triggerUpload as triggerUploadUtil,
  type MovementError,
  type Resource as ResouceForAType,
  type ResourceType,
} from "./utils";

const asDateOrNull = (v: unknown) => (v instanceof Date && !isNaN(v.getTime()) ? v : null);

const isAfterCurrentMinute = (value: Date) => {
  const valueMinute = new Date(value);
  const nowMinute = new Date();
  valueMinute.setSeconds(0, 0);
  nowMinute.setSeconds(0, 0);
  return valueMinute.getTime() > nowMinute.getTime();
};

// Helper function to safely parse date and return ISO string or undefined
const safeDateToISO = (dateValue: string | Date | null | undefined): string | undefined => {
  if (!dateValue) return undefined;
  try {
    const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
    return !isNaN(date.getTime()) ? date.toISOString() : undefined;
  } catch {
    return undefined;
  }
};

// Helper function to safely format date for display
const safeFormatDate = (dateValue: string | Date | null | undefined): string | null => {
  if (dateValue === "") return "";
  if (!dateValue) return null;
  try {
    const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
  } catch {
    // Invalid date
  }
  return null;
};

const mapRefValue = (
  value: string | null | undefined,
  map: ReadonlyMap<string, string>,
): string | null | undefined => {
  if (!value) return value;
  return map.get(value) ?? value;
};

const normalizeCoordinateMinute = (value: string): string => {
  if (!value) return "";

  const sanitizedValue = value.replace(/[^\d.]/g, "");
  const [wholePart = "", decimalPart = ""] = sanitizedValue.split(".");
  const minute = Math.min(Number.parseInt(wholePart || "0", 10), 59);

  return `${minute}${sanitizedValue.includes(".") ? `.${decimalPart.slice(0, 2)}` : ""}`;
};

const PILOTAGE_TYPE_OTHER = "Others";
const NOTES_MAX_CHARS = 1000;
const SHACKLES_MAX_DIGITS = 9;

const pilotageTypeOptions: AutocompleteOption[] = [
  { label: "Normal", value: "Normal" },
  { label: "Dead Vessel", value: "Dead Vessel" },
  { label: "Deficiencies", value: "Deficiencies" },
  { label: "Weather", value: "Weather" },
  { label: "Others", value: PILOTAGE_TYPE_OTHER },
];

interface CloseMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}

interface OutletContextType {
  vesselCallDetails: Pick<VesselCallResponse, "id" | "vesselId" | "eta" | "lastTaskDecision">;
  setFakeStatus?: (status: string | null) => void;
}

export interface Resource {
  additionalResourceType: string;
  numberOfResources: number;
  startTimeOfUsage?: string | null;
  endTimeOfUsage?: string | null;
  resourceName?: string;
  connectionTime?: string | null;
  location?: string;
  id?: string;
}

const serviceVesselCall = "bff-vessel-call";

type UpdatedTimes = {
  eta?: Date | null;
  ets?: Date | null;
  pilotUnderway?: Date | null;
  permissionToSail?: Date | null;
  anchorTime?: Date | null;
  anchorAweighTime?: Date | null;
  pob?: Date | null;
  firstLineAshore?: Date | null;
  allFast?: Date | null;
  pilotDisembark?: Date | null;
  ats?: Date | null;
  allLinesClear?: Date | null;
  etc?: Date | null;
  allClear?: Date | null;
  etd?: Date | null;
  pilotStationCode?: string | null;
};

type UpdateTimesField = keyof UpdatedTimes;
type UpdateTimesLabelField = UpdateTimesField | "atc";

type UpdateTimesConfig = {
  visibleFields: ReadonlyArray<UpdateTimesField>;
  labelKeys?: Partial<Record<UpdateTimesLabelField, string>>;
};

const UPDATE_TIME_DEFAULT_LABEL_KEYS: Record<UpdateTimesLabelField, string> = {
  eta: "vessel-call.movement-services.update-form.fields.eta-readiness",
  etd: "vessel-call.movement-services.update-form.fields.etd-readiness",
  ets: "vessel-call.movement-services.update-form.fields.ets-readiness",
  etc: "vessel-call.movement-services.update-form.fields.etc-completion",
  pilotUnderway: "vessel-call.movement-services.update-form.fields.pilot-underway",
  permissionToSail: "vessel-call.movement-services.update-form.fields.permission-to-sail",
  anchorTime: "vessel-call.movement-services.update-form.fields.anchor-time",
  anchorAweighTime: "vessel-call.movement-services.update-form.fields.anchor-aweigh-time",
  pob: "vessel-call.movement-services.update-form.fields.pob",
  firstLineAshore: "vessel-call.movement-services.update-form.fields.first-line-ashore",
  allFast: "vessel-call.movement-services.update-form.fields.all-fast",
  pilotDisembark: "vessel-call.movement-services.update-form.fields.pilot-disembarked",
  ats: "vessel-call.movement-services.update-form.fields.actual-time-start",
  allLinesClear: "vessel-call.movement-services.update-form.fields.all-lines-clear",
  allClear: "vessel-call.movement-services.update-form.fields.all-clear",
  pilotStationCode: "vessel-call.movement-services.update-form.fields.pilot-boarding-area",
  atc: "vessel-call.movement-services.close-form.fields.actual-time-completion",
};

// Visible fields for Closure drawer in the Times and Estimates section
const TIMES_ESTIMATES_UPDATE: Partial<Record<string, UpdateTimesConfig>> = {
  SEA_TO_BERTH: {
    visibleFields: [
      "eta",
      "ets",
      "etc",
      "pilotStationCode",
      "pilotUnderway",
      "pob",
      "firstLineAshore",
      "allFast",
      "pilotDisembark",
      "ats",
    ],
    labelKeys: {
      pob: "vessel-call.movement-services.update-form.fields.pilot-on-board",
      atc: "vessel-call.movement-services.close-form.fields.actual-time-completion",
    },
  },
  BERTH_TO_SEA: {
    visibleFields: [
      "etd",
      "ets",
      "etc",
      "pilotUnderway",
      "pob",
      "allClear",
      "pilotDisembark",
      "ats",
    ],
    labelKeys: {
      etd: "vessel-call.movement-services.update-form.fields.etd-readiness",
      ets: "vessel-call.movement-services.update-form.fields.ets-readiness",
      etc: "vessel-call.movement-services.update-form.fields.etc-completion",
      pob: "vessel-call.movement-services.update-form.fields.pilot-on-board",
      allClear: "vessel-call.movement-services.update-form.fields.all-clear",
      pilotDisembark: "vessel-call.movement-services.update-form.fields.pilot-disembarked",
      ats: "vessel-call.movement-services.update-form.fields.actual-time-start",
      atc: "vessel-call.movement-services.close-form.fields.actual-time-completion",
    },
  },
  ANCHORAGE_TO_SEA: {
    visibleFields: ["etd", "ats"],
    labelKeys: {
      etd: "vessel-call.movement-services.update-form.fields.etd-readiness",
      ats: "vessel-call.movement-services.update-form.fields.ats-permission-to-sail",
      atc: "vessel-call.movement-services.close-form.fields.atc-anchor-aweigh-time",
    },
  },
  ANCHORAGE_TO_BERTH: {
    visibleFields: [
      "eta",
      "ets",
      "etc",
      "pilotStationCode",
      "anchorAweighTime",
      "pilotUnderway",
      "pob",
      "firstLineAshore",
      "allFast",
      "pilotDisembark",
      "ats",
    ],
    labelKeys: {
      eta: "vessel-call.movement-services.update-form.fields.eta-readiness",
      ets: "vessel-call.movement-services.update-form.fields.ets-readiness",
      anchorAweighTime: "vessel-call.movement-services.update-form.fields.anchor-aweigh-time",
      pob: "vessel-call.movement-services.update-form.fields.pilot-on-board",
      pilotDisembark: "vessel-call.movement-services.update-form.fields.pilot-disembarked",
      ats: "vessel-call.movement-services.update-form.fields.actual-time-start",
      pilotStationCode: "vessel-call.movement-services.update-form.fields.pilot-boarding-area",
      atc: "vessel-call.movement-services.close-form.fields.actual-time-completion",
    },
  },
  SEA_TO_ANCHORAGE: {
    visibleFields: ["eta", "ats"],
    labelKeys: {
      eta: "vessel-call.movement-services.update-form.fields.eta-readiness",
      ats: "vessel-call.movement-services.update-form.fields.ats-permission-to-anchor",
      atc: "vessel-call.movement-services.close-form.fields.atc-anchor-time",
    },
  },
  ANCHORAGE_TO_ANCHORAGE: {
    visibleFields: ["ets", "ats"],
    labelKeys: {
      ets: "vessel-call.movement-services.update-form.fields.ets-permission-to-heave-anchor",
      ats: "vessel-call.movement-services.update-form.fields.ats-anchor-aweigh-underway",
      atc: "vessel-call.movement-services.close-form.fields.atc-anchor-time",
    },
  },
  BERTH_TO_BERTH: {
    visibleFields: [
      "etd",
      "eta",
      "ets",
      "etc",
      "pilotUnderway",
      "pob",
      "allClear",
      "firstLineAshore",
      "allFast",
      "pilotDisembark",
      "ats",
    ],
    labelKeys: {
      etd: "vessel-call.movement-services.update-form.fields.etd-readiness",
      eta: "vessel-call.movement-services.update-form.fields.eta-readiness",
      ets: "vessel-call.movement-services.update-form.fields.ets-readiness",
      etc: "vessel-call.movement-services.update-form.fields.etc-completion",
      pob: "vessel-call.movement-services.update-form.fields.pilot-on-board",
      allClear: "vessel-call.movement-services.update-form.fields.all-clear",
      pilotDisembark: "vessel-call.movement-services.update-form.fields.pilot-disembarked",
      ats: "vessel-call.movement-services.update-form.fields.actual-time-start",
      atc: "vessel-call.movement-services.close-form.fields.actual-time-completion",
    },
  },
  BERTH_TO_ANCHORAGE: {
    visibleFields: [
      "etd",
      "ets",
      "pilotUnderway",
      "pob",
      "etc",
      "allClear",
      "pilotDisembark",
      "anchorTime",
      "ats",
    ],
    labelKeys: {
      etd: "vessel-call.movement-services.update-form.fields.etd-readiness",
      ets: "vessel-call.movement-services.update-form.fields.ets-readiness",
      pob: "vessel-call.movement-services.update-form.fields.pilot-on-board",
      etc: "vessel-call.movement-services.update-form.fields.etc-completion",
      allClear: "vessel-call.movement-services.update-form.fields.all-clear",
      pilotDisembark: "vessel-call.movement-services.update-form.fields.pilot-disembarked",
      anchorTime: "vessel-call.movement-services.update-form.fields.vessel-anchor-time",
      ats: "vessel-call.movement-services.update-form.fields.actual-time-start",
      atc: "vessel-call.movement-services.close-form.fields.actual-time-completion",
    },
  },
};

const getResourcesValidationError = (resources: Resource[], t: (key: string) => string) => {
  const resourcesWithTimesAndName = resources.filter(
    (resource) =>
      resource.startTimeOfUsage && resource.endTimeOfUsage && resource.resourceName?.trim(),
  );

  if (resources.length > 0 && resourcesWithTimesAndName.length !== resources.length) {
    return t("vessel-call.movement-services.close-form.errors.resources-times-required");
  }

  return undefined;
};

const CloseMovementService: React.FC<CloseMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { vesselCallDetails, setFakeStatus } = useOutletContext<OutletContextType>();
  const vesselId = vesselCallDetails?.vesselId;
  const vesselCallId = vesselCallDetails?.id;
  const { userAccessPermissions } = useAuthStore();
  const hasInternalNotesAccess = checkPermission(userAccessPermissions, "hasInternalNotesAccess");

  const currentMovement = currentMovements?.[0];
  const movementServiceId = currentMovement?.id;

  // Fetch movement service details for prepopulation
  const { data: movementDetails, refetch: refetchMovementDetails } =
    useQuery<MovementServiceDetailsBackendResponse>({
      queryKey: ["movement-service-close-drawer", movementServiceId],
      queryFn: () =>
        getMovementServiceDetailsById({
          id: movementServiceId!,
          service: serviceVesselCall,
        }),
      enabled: !!movementServiceId && isOpen,
    });

  const { options: resourceTypes } = useFormattedReference<ResourceType>(
    REFERENCE_DATA_TYPES.RESOURCE_TYPES,
    "bff-vessel-call",
  );

  const resourceTypesMap = useMemo(() => {
    const map = new Map<string, string>();
    resourceTypes.forEach(({ resourceTypeCode, resourceTypeDesc }) => {
      map.set(resourceTypeCode, resourceTypeDesc);
    });
    return map;
  }, [resourceTypes]);

  const { options: resourcesForAType } = useFormattedReference<ResouceForAType>(
    REFERENCE_DATA_TYPES.RESOURCE,
    "bff-vessel-call",
  );

  const resourcesMap = useMemo(() => {
    const map = new Map<string, string>();
    resourcesForAType.forEach(({ resourceCode, resourceName }) => {
      map.set(resourceCode, resourceName);
    });
    return map;
  }, [resourcesForAType]);

  // Refetch movement details when drawer opens to get latest updates
  useEffect(() => {
    if (isOpen && movementServiceId) {
      void refetchMovementDetails();
    }
  }, [isOpen, movementServiceId, refetchMovementDetails]);

  const [atc, setAtc] = useState<Date | null>(null);
  const [isAtcChanged, setIsAtcChanged] = useState(false);
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [vesselAnchoredShacklesInWater, setVesselAnchoredShacklesInWater] = useState<string>("");
  const [permissionToSailTime, setPermissionToSailTime] = useState<Date | null>(null);
  const [isPermissionToSailTimeChanged, setIsPermissionToSailTimeChanged] = useState(false);
  const [pilotageType, setPilotageType] = useState<AutocompleteOption | null>(null);
  const [specifyIfOthers, setSpecifyIfOthers] = useState<string>("");
  const [externalNotes, setExternalNotes] = useState<string>("");
  const [internalNotes, setInternalNotes] = useState<string>("");
  const [resources, setResources] = useState<Resource[]>([]);
  const [atcError, setAtcError] = useState<string | null>(null);
  const [additionalErrors, setAdditionalErrors] = useState<
    Partial<Record<UpdateTimesField, string>> & {
      latitude?: string;
      longitude?: string;
      location?: string;
      vesselAnchoredShacklesInWater?: string;
      permissionToSailTime?: string;
      resources?: string;
      pilotageType?: string;
      specifyIfOthers?: string;
    }
  >({});

  // State for updated times
  const [updatedTimes, setUpdatedTimes] = useState<UpdatedTimes>({});

  const [isAddResourceDrawerOpen, setIsAddResourceDrawerOpen] = useState(false);
  const [isEditResourceDrawerOpen, setIsEditResourceDrawerOpen] = useState(false);
  const [editingResourceIndex, setEditingResourceIndex] = useState<number | null>(null);
  const [isUpdateTimesDrawerOpen, setIsUpdateTimesDrawerOpen] = useState(false);

  // File upload
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const externalAttachmentsFiles = useUploadStore(
    (state) => state.filesByKey.closeMovementExternalAttachments ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalAttachmentsFiles = useUploadStore(
    (state) => state.filesByKey.closeMovementInternalAttachments ?? EMPTY_UPLOAD_ENTRIES,
  );
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const externalAttachmentsUploadSignatureRef = useRef<string | null>(null);
  const internalAttachmentsUploadSignatureRef = useRef<string | null>(null);
  const [externalAttachmentsUrl, setExternalAttachmentsUrl] = useState<string>();
  const [internalAttachmentsUrl, setInternalAttachmentsUrl] = useState<string>();

  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);

  const setExternalAttachmentsFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("closeMovementExternalAttachments");
        return;
      }
      setFilesForKey("closeMovementExternalAttachments", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const setInternalAttachmentsFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("closeMovementInternalAttachments");
        return;
      }
      setFilesForKey("closeMovementInternalAttachments", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  useEffect(() => {
    setExternalAttachmentsFiles([]);
    setInternalAttachmentsFiles([]);
  }, []);

  const displayedResources = useMemo(
    () =>
      resources.map((resource) => ({
        ...resource,
        additionalResourceType:
          mapRefValue(resource.additionalResourceType, resourceTypesMap) ?? "",
        resourceName: mapRefValue(resource.resourceName, resourcesMap) ?? undefined,
      })),
    [resources, resourceTypesMap, resourcesMap],
  );

  const resetFormValues = () => {
    setAtc(null);
    setIsAtcChanged(false);
    setLatitude("");
    setLongitude("");
    setLocation("");
    setVesselAnchoredShacklesInWater("");
    setPermissionToSailTime(null);
    setIsPermissionToSailTimeChanged(false);
    setPilotageType(null);
    setSpecifyIfOthers("");
    setExternalNotes("");
    setInternalNotes("");
    setResources([]);
    setAtcError(null);
    setAdditionalErrors({});
    setUpdatedTimes({});
    setExternalAttachmentsUrl("");
    setInternalAttachmentsUrl("");
    setExternalAttachmentsFiles([]);
    setInternalAttachmentsFiles([]);
  };

  // Determine movement destination
  const isToFoaa = useMemo(() => {
    const to = currentMovement?.to?.toUpperCase() || movementDetails?.to?.toUpperCase() || "";
    return to === "FOAA" || to === "DOAA" || to.includes("ANCHORAGE");
  }, [currentMovement?.to, movementDetails?.to]);

  const toAnchorageLocation = currentMovement?.to ?? movementDetails?.to;
  const { data: anchorageAreas } = useQuery({
    queryKey: ["anchorageAreas"],
    queryFn: () => getAnchorageAreas(serviceVesselCall),
    enabled: isToFoaa,
  });
  const anchorageOptions: AutocompleteOption[] =
    anchorageAreas
      ?.filter((option) => option.fromtoloc?.toLowerCase() === toAnchorageLocation?.toLowerCase())
      .map((option) => ({
        value: option.anchorageAreaCode,
        label: option.anchorageAreaDesc,
      })) ?? [];

  const isToSea = useMemo(() => {
    const to = currentMovement?.to?.toUpperCase() || movementDetails?.to?.toUpperCase() || "";
    return to === "SEA";
  }, [currentMovement?.to, movementDetails?.to]);

  const movementType = currentMovement?.movementType ?? movementDetails?.movementType;
  const updateTimesConfig = movementType ? TIMES_ESTIMATES_UPDATE[movementType] : undefined;

  const isSeaToBerth = movementType === "SEA_TO_BERTH";
  const isBerthToSea = movementType === "BERTH_TO_SEA";
  const isAnchorageToSea = movementType === "ANCHORAGE_TO_SEA";
  const isAnchorageToBerth = movementType === "ANCHORAGE_TO_BERTH";
  const isAnchorageToAnchorage = movementType === "ANCHORAGE_TO_ANCHORAGE";
  const isSeaToAnchorage = movementType === "SEA_TO_ANCHORAGE";
  const isBerthToBerth = movementType === "BERTH_TO_BERTH";
  const isBerthToAnchorage = movementType === "BERTH_TO_ANCHORAGE";

  const atcLabel = t(
    `vessel-call.movement-services.close-form.fields.${
      isSeaToBerth || isBerthToSea || isAnchorageToBerth || isBerthToBerth || isBerthToAnchorage
        ? "actual-time-completion"
        : isAnchorageToSea
          ? "atc-anchor-aweigh-time"
          : isSeaToAnchorage || isAnchorageToAnchorage
            ? "atc-anchor-time"
            : "atc"
    }`,
  );

  const etaSource = isSeaToAnchorage
    ? (movementDetails?.eta ?? vesselCallDetails.eta)
    : movementDetails?.eta;
  const atsSource = isSeaToAnchorage
    ? (movementDetails?.ats ?? vesselCallDetails.lastTaskDecision?.timestamp)
    : movementDetails?.ats;
  const atsForResourceValidation = useMemo(() => {
    const ats = updatedTimes.ats ?? (atsSource ? new Date(atsSource) : null);
    return ats && !isNaN(ats.getTime()) ? ats : null;
  }, [atsSource, updatedTimes.ats]);

  const isToBerth = useMemo(() => {
    // Check movement type first
    const movementType =
      (currentMovement as any)?.movementType || (movementDetails as any)?.movementType;
    const toBerthTypes = ["SEA_TO_BERTH", "ANCHORAGE_TO_BERTH", "BERTH_TO_BERTH"];
    if (movementType && toBerthTypes.includes(movementType)) {
      return true;
    }

    // Check if destination is a terminal location
    const to = currentMovement?.to?.toUpperCase() || movementDetails?.to?.toUpperCase() || "";
    if (to) {
      const terminalCodes = ["FUJT", "OPS", "FOTT", "MAR", "VPK", "ADNOC", "DIBMAR", "DIBOPS"];
      return terminalCodes.includes(to) || to.includes("TERMINAL");
    }
    return false;
  }, [currentMovement, movementDetails]);

  const isFromBerthToBerth = useMemo(() => {
    const from = currentMovement?.from?.toUpperCase() || movementDetails?.from?.toUpperCase() || "";
    const to = currentMovement?.to?.toUpperCase() || movementDetails?.to?.toUpperCase() || "";
    const terminalCodes = ["FUJT", "OPS", "FOTT", "MAR", "VPK", "ADNOC", "DIBMAR", "DIBOPS"];
    const isFromTerminal = terminalCodes.includes(from) || from.includes("TERMINAL");
    const isToTerminal = terminalCodes.includes(to) || to.includes("TERMINAL");
    return isFromTerminal && isToTerminal && isToBerth;
  }, [currentMovement?.from, currentMovement?.to, movementDetails, isToBerth]);

  const updateTimesVisibleFields = useMemo<ReadonlyArray<UpdateTimesField>>(() => {
    if (updateTimesConfig) {
      return updateTimesConfig.visibleFields;
    }

    if (isToFoaa) {
      return ["anchorTime", "anchorAweighTime"];
    }

    if (isToBerth) {
      return ["pob", "allFast", "allLinesClear"];
    }

    if (isToSea) {
      return ["permissionToSail"];
    }

    return [];
  }, [updateTimesConfig, isToFoaa, isToBerth, isToSea]);

  // Prepopulate fields from movement details
  useEffect(() => {
    if (!movementDetails || !isOpen) return;

    const initialAtc =
      isSeaToBerth || isAnchorageToBerth || isBerthToBerth
        ? movementDetails.allFastTime
        : isBerthToSea || isBerthToAnchorage
          ? movementDetails.allClearTime
          : movementDetails.atc;

    if (initialAtc) {
      try {
        const atcDate = new Date(initialAtc);
        if (!isNaN(atcDate.getTime())) {
          setAtc(atcDate);
        }
      } catch {
        // Ignore invalid dates
      }
    }

    // Prepopulate coordinates if available
    if (movementDetails.latitude) {
      setLatitude(String(movementDetails.latitude));
    }
    if (movementDetails.longitude) {
      setLongitude(String(movementDetails.longitude));
    }
    if (movementDetails.designatedArea) {
      setLocation(movementDetails.designatedArea);
    }

    // Prepopulate vessel anchored shackles if available
    const vesselAnchoredShackles =
      movementDetails.vesselAnchoredShacklesInWater ??
      movementDetails.vesselAnchoredShackles ??
      movementDetails.timeEstimates?.vesselAnchoredShackles ??
      null;
    if (vesselAnchoredShackles != null) {
      setVesselAnchoredShacklesInWater(String(vesselAnchoredShackles));
    }

    // Prepopulate permission to sail time if available
    const permissionToSail =
      (movementDetails as any)?.permissionToSailTime || (movementDetails as any)?.permissionToSail;
    if (permissionToSail) {
      try {
        const permissionToSailDate = new Date(permissionToSail);
        if (!isNaN(permissionToSailDate.getTime())) {
          setPermissionToSailTime(permissionToSailDate);
        }
      } catch {
        // Ignore invalid dates
      }
    }

    // Prepopulate times from movement details
    const timesToSet: UpdatedTimes = {};

    // Helper function to safely parse date
    const parseDate = (dateValue: string | null | undefined): Date | null => {
      if (!dateValue) return null;
      try {
        const date = new Date(dateValue);
        return !isNaN(date.getTime()) ? date : null;
      } catch {
        return null;
      }
    };

    const eta = parseDate(etaSource);
    const etd = parseDate(movementDetails.etd);
    const ets = parseDate(movementDetails.ets);
    const etc = parseDate(movementDetails.etc);
    const pilotUnderwayTime = parseDate(movementDetails.pilotUnderwayTime);
    const pobTime = parseDate(movementDetails.pobTime);
    const firstLineAshoreTime = parseDate(movementDetails.firstLineAshoreTime);
    const allFastTime = parseDate(movementDetails.allFastTime);
    const pilotDisembarkTime = parseDate(movementDetails.pilotDisembarkTime);
    const ats = parseDate(atsSource);
    // For FROM BERTH TO BERTH, read allClearTime
    const allClearTime =
      isBerthToBerth || isBerthToSea ? parseDate((movementDetails as any)?.allClearTime) : null;
    const allLinesClearTime = isBerthToBerth
      ? null
      : parseDate((movementDetails as any)?.allLinesClearTime);
    const anchorTime = parseDate((movementDetails as any)?.anchorTime);
    const anchorAweighTime = parseDate((movementDetails as any)?.anchorAweighTime);
    const pilotStationCode = movementDetails?.pilotStationCode;

    if (eta) timesToSet.eta = eta;
    if (etd) timesToSet.etd = etd;
    if (ets) timesToSet.ets = ets;
    if (etc) timesToSet.etc = etc;
    if (pilotUnderwayTime) timesToSet.pilotUnderway = pilotUnderwayTime;
    if (pobTime) timesToSet.pob = pobTime;
    if (firstLineAshoreTime) timesToSet.firstLineAshore = firstLineAshoreTime;
    if (allFastTime) timesToSet.allFast = allFastTime;
    if (pilotDisembarkTime) timesToSet.pilotDisembark = pilotDisembarkTime;
    if (ats) timesToSet.ats = ats;
    if (allClearTime) {
      timesToSet.allClear = allClearTime;
    }
    if (allLinesClearTime) timesToSet.allLinesClear = allLinesClearTime;
    if (anchorTime) timesToSet.anchorTime = anchorTime;
    if (anchorAweighTime) timesToSet.anchorAweighTime = anchorAweighTime;
    if (pilotStationCode) timesToSet.pilotStationCode = pilotStationCode;

    // Set updatedTimes if any times were found
    if (Object.keys(timesToSet).length > 0) {
      setUpdatedTimes(timesToSet);
    }

    // Prepopulate resources from movement request
    if (movementDetails.additionalResources && movementDetails.additionalResources.length > 0) {
      const prepopulatedResources: Resource[] = movementDetails.additionalResources.map((res) => {
        const resAny = res as any;

        return {
          additionalResourceType: res.additionalResourceType,
          numberOfResources: res.numberOfResources,
          id: `prepop-${Math.random()}`,
          // Include existing time fields if available
          ...(resAny.startTimeOfUsage && { startTimeOfUsage: resAny.startTimeOfUsage }),
          ...(resAny.endTimeOfUsage && { endTimeOfUsage: resAny.endTimeOfUsage }),
          ...(resAny.resourceName != null &&
            resAny.resourceName !== "" && { resourceName: resAny.resourceName }),
          ...(resAny.connectionTime && { connectionTime: resAny.connectionTime }),
          ...(resAny.location && { location: resAny.location }),
        };
      });
      setResources(prepopulatedResources);
    }

    if (movementDetails.task?.externalNotes) {
      setExternalNotes(movementDetails.task.externalNotes);
    }

    if (
      movementDetails.task?.externalNotesDocument &&
      movementDetails.task?.externalNotesDocumentName
    ) {
      setExternalAttachmentsUrl(movementDetails.task.externalNotesDocument);
    }

    // Prepopulate internal notes if available
    if (hasInternalNotesAccess && movementDetails.task?.internalNotes) {
      setInternalNotes(movementDetails.task.internalNotes);
    }

    // Prepopulate internal attachments if available
    if (
      hasInternalNotesAccess &&
      movementDetails.task?.internalNotesDocument &&
      movementDetails.task?.internalNotesDocumentName
    ) {
      setInternalAttachmentsUrl(movementDetails.task.internalNotesDocument);
    }

    const movementPilotageType = movementDetails.pilotageType;
    if (movementPilotageType) {
      const option = pilotageTypeOptions.find((item) => item.value === movementPilotageType);
      setPilotageType(
        option ?? { label: String(movementPilotageType), value: movementPilotageType },
      );
    }

    const movementSpecifyIfOthers = movementDetails.specifyIfOthers;
    if (movementSpecifyIfOthers) {
      setSpecifyIfOthers(String(movementSpecifyIfOthers));
    }
  }, [
    movementDetails,
    isOpen,
    hasInternalNotesAccess,
    isSeaToBerth,
    isBerthToSea,
    isBerthToBerth,
    isBerthToAnchorage,
    isFromBerthToBerth,
    isAnchorageToBerth,
    etaSource,
    atsSource,
  ]);

  const inputSlotProps = makeInputSlotProps(theme);
  const sxProps = makeInputSxProps(theme);
  const alignedInputSxProps = {
    ...sxProps,
    "width": "100%",
    "& .MuiOutlinedInput-input": {
      paddingLeft: 0,
      paddingRight: 0,
    },
  };
  const coordinateInputSxProps = {
    ...alignedInputSxProps,
    "& .MuiOutlinedInput-input": {
      ...alignedInputSxProps["& .MuiOutlinedInput-input"],
      flex: "0 0 5ch",
      marginLeft: "auto",
      width: "5ch",
    },
  };

  const pilotageTypeLabelText = t("vessel-call.movement-services.close-form.fields.pilotage-type");
  const specifyIfOthersLabelText = t(
    "vessel-call.movement-services.close-form.fields.specify-if-others",
  );

  const isOtherPilotageType = pilotageType?.value === PILOTAGE_TYPE_OTHER;
  const specifyIfOthersSlotProps = {
    ...inputSlotProps,
    htmlInput: {
      ...inputSlotProps.htmlInput,
      maxLength: 50,
    },
  };
  const notesSlotProps = {
    ...inputSlotProps,
    htmlInput: {
      ...inputSlotProps.htmlInput,
      maxLength: NOTES_MAX_CHARS,
    },
  };
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const handlePilotageTypeChange = (option: AutocompleteOption | null) => {
    const selectedOther = option?.value === PILOTAGE_TYPE_OTHER;

    setPilotageType(option);
    if (!selectedOther) {
      setSpecifyIfOthers("");
    }

    if (additionalErrors.pilotageType || additionalErrors.specifyIfOthers) {
      setAdditionalErrors((prev) => ({
        ...prev,
        pilotageType: undefined,
        specifyIfOthers: selectedOther ? prev.specifyIfOthers : undefined,
      }));
    }
  };

  const handleSpecifyIfOthersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSpecifyIfOthers(event.target.value);
    if (additionalErrors.specifyIfOthers) {
      setAdditionalErrors((prev) => ({ ...prev, specifyIfOthers: undefined }));
    }
  };

  const documentUploadMutation = useMutation({
    mutationFn: async ({ file, documentType }: { file: File; documentType: string }) => {
      return runTransaction(
        {
          module: "vessel_call",
          screen: "CloseMovementService",
          action: "upload_movement_service_document",
          entityType: "movement_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken: {
            prefix: SasTokenPrefix;
            contentType: string | null;
            privilege: SasTokenPrivilege;
            blobUrl: string | null;
          } = {
            prefix: SasTokenPrefix.MOVEMENT_SERVICE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };
          const sasTokenResponse = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          return uploadFileToAzureBlob({
            blobUrl: sasTokenResponse.blobUrl,
            sasToken: sasTokenResponse.sasToken,
            file,
            certificateType: documentType,
          });
        },
      );
    },
    onSuccess: (data, variables) => {
      // uploadFileToAzureBlob returns an array, get the first element
      if (data && data.length > 0) {
        if (variables.documentType === "EXTERNAL_ATTACHMENT") {
          setExternalAttachmentsUrl(data[0].documentUrl);
        } else {
          setInternalAttachmentsUrl(data[0].documentUrl);
        }
      }
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const handleExternalAttachmentsUpload = useCallback(
    (file: File) => {
      documentUploadMutation.mutate({ file, documentType: "EXTERNAL_ATTACHMENT" });
    },
    [documentUploadMutation],
  );

  const handleInternalAttachmentsUpload = useCallback(
    (file: File) => {
      documentUploadMutation.mutate({ file, documentType: "INTERNAL_ATTACHMENT" });
    },
    [documentUploadMutation],
  );

  const handleSuccess = async () => {
    // Set fake status immediately to update UI
    setFakeStatus?.("CLOSED");

    await queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });
    // Invalidate both query keys - the preview component and the close drawer use different keys
    await queryClient.invalidateQueries({
      queryKey: ["movement-service-preview", movementServiceId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["movement-service-close-drawer", movementServiceId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["getPastMovementServices"],
    });

    onClose("closeDrawer");
    resetFormValues();
    notify({
      title: t("vessel-call.movement-services.movement-closed"),
      description: t("vessel-call.movement-services.movement-closed-description"),
      type: "success",
    });

    // Refetch after delay to get real status from BE
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", movementServiceId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["getPastMovementServices"],
      });
      // Clear fake status after refetch
      setFakeStatus?.(null);
    }, 2000);
  };

  const handleCloseDrawer = () => {
    onClose("closeDrawer");
    resetFormValues();
  };

  const handleError = async (error: MovementError) => {
    await queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });
    // Invalidate both query keys - the preview component and the close drawer use different keys
    await queryClient.invalidateQueries({
      queryKey: ["movement-service-preview", movementServiceId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["movement-service-close-drawer", movementServiceId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["getPastMovementServices"],
    });
    notify({
      title: t("vessel-call.movement-services.movement-close-error"),
      description:
        error?.response?.data?.detail ??
        error?.message ??
        t("vessel-call.movement-services.movement-close-error-description"),
      type: "error",
    });
    if (error.status === 409) {
      handleCloseDrawer();
    }
  };

  const { closeMovementService, isSubmitting } = useCloseMovementService({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    if (!movementServiceId) return;

    const latitudeLabel = t("vessel-call.movement-services.close-form.fields.latitude");
    const longitudeLabel = t("vessel-call.movement-services.close-form.fields.longitude");
    const locationLabel = t("vessel-call.movement-services.close-form.fields.location");
    const vesselAnchoredShacklesLabel = t(
      "vessel-call.movement-services.close-form.fields.vessel-anchored-shackles",
    );
    const permissionToSailLabel = t(
      "vessel-call.movement-services.close-form.fields.permission-to-sail",
    );
    const pilotageTypeLabel = t("vessel-call.movement-services.close-form.fields.pilotage-type");
    const specifyIfOthersLabel = t(
      "vessel-call.movement-services.close-form.fields.specify-if-others",
    );
    const atsLabel = t(
      updateTimesConfig?.labelKeys?.ats ??
        "vessel-call.movement-services.update-form.fields.actual-time-start",
    );

    // Base schema - ATC is always required
    const baseSchema = z.object({
      atc: z.date({
        required_error: t("form-validation.required", { label: atcLabel }),
        invalid_type_error: t("form-validation.required", { label: atcLabel }),
      }),
    });

    // FOAA schema
    const foaaSchema = z.object({
      latitude: z
        .string()
        .min(1, t("form-validation.required", { label: latitudeLabel }))
        .refine((val) => /^(?:[0-9]|[1-5][0-9])(?:\.\d{1,2})?$/.test(val), {
          message: t("form-validation.invalid", { label: latitudeLabel }),
        }),
      longitude: z
        .string()
        .min(1, t("form-validation.required", { label: longitudeLabel }))
        .refine((val) => /^(?:[0-9]|[1-5][0-9])(?:\.\d{1,2})?$/.test(val), {
          message: t("form-validation.invalid", { label: longitudeLabel }),
        }),
      vesselAnchoredShacklesInWater: z
        .string()
        .min(1, t("form-validation.required", { label: vesselAnchoredShacklesLabel }))
        .max(
          SHACKLES_MAX_DIGITS,
          t("form-validation.max-length-digits", {
            label: vesselAnchoredShacklesLabel,
            maxDigits: SHACKLES_MAX_DIGITS,
          }),
        )
        .refine((val) => /^\d+$/.test(val), {
          message: t("form-validation.invalid", { label: vesselAnchoredShacklesLabel }),
        }),
    });
    const locationSchema = z
      .string()
      .min(1, t("form-validation.required", { label: locationLabel }));

    const pilotageSchema = z.object({
      pilotageType:
        isAnchorageToSea || isSeaToAnchorage || isAnchorageToAnchorage
          ? z
              .object({
                label: z.string(),
                value: z.union([z.string(), z.number()]),
              })
              .optional()
          : z.object({
              label: z.string(),
              value: z.union([z.string(), z.number()]),
            }),
    });

    const specifyIfOthersSchema = z.object({
      specifyIfOthers: z
        .string()
        .trim()
        .min(1, t("form-validation.required", { label: specifyIfOthersLabel }))
        .max(50, t("form-validation.max-length", { label: specifyIfOthersLabel, max: 50 })),
    });

    // SEA schema
    // @TODO Remove if not needed
    // const seaSchema = z.object({
    //   permissionToSailTime: z.date().optional(),
    // });

    // Validate all fields and collect all errors
    const errors: typeof additionalErrors = {};
    const existingTimeValues: Record<UpdateTimesField, unknown> = {
      eta: etaSource,
      etd: movementDetails?.etd,
      ets: movementDetails?.ets,
      etc: movementDetails?.etc,
      pilotUnderway: movementDetails?.pilotUnderwayTime,
      permissionToSail: permissionToSailTime,
      anchorTime: movementDetails?.anchorTime,
      anchorAweighTime: movementDetails?.anchorAweighTime,
      pob: movementDetails?.pobTime,
      firstLineAshore: movementDetails?.firstLineAshoreTime,
      allFast: movementDetails?.allFastTime,
      pilotDisembark: movementDetails?.pilotDisembarkTime,
      ats: atsSource,
      allLinesClear: movementDetails?.allLinesClearTime,
      allClear: movementDetails?.allClearTime,
      pilotStationCode: movementDetails?.pilotStationCode,
    };
    const timeValues: UpdatedTimes = {};
    updateTimesVisibleFields.forEach((fieldName) => {
      const value = updatedTimes[fieldName] ?? existingTimeValues[fieldName];
      if (fieldName === "pilotStationCode") {
        timeValues[fieldName] = typeof value === "string" ? value : null;
      } else {
        const date =
          value instanceof Date ? value : typeof value === "string" ? new Date(value) : null;
        timeValues[fieldName] = asDateOrNull(date);
      }
    });
    Object.assign(
      errors,
      validateMovementTimes({
        values: timeValues,
        visibleFields: updateTimesVisibleFields,
        getLabel: (fieldName) =>
          fieldName === "atc"
            ? atcLabel
            : t(
                updateTimesConfig?.labelKeys?.[fieldName] ??
                  UPDATE_TIME_DEFAULT_LABEL_KEYS[fieldName],
              ),
        t,
        atc,
        earliestResourceStartTime: getEarliestResourceStartTime(resources),
      }),
    );

    // Validate ATC (always required)
    const atcValidation = baseSchema.safeParse({ atc: atc ?? undefined });
    let isAtcValid = atcValidation.success;
    if (!atcValidation.success) {
      setAtcError(
        atcValidation.error.errors[0]?.message ??
          t("form-validation.required", { label: atcLabel }),
      );
    } else {
      const ats = updatedTimes.ats ?? (atsSource ? new Date(atsSource) : null);
      if (isAtcChanged && atc && isAfterCurrentMinute(atc)) {
        setAtcError(t("form-validation.must-not-be-in-future", { label: atcLabel }));
        isAtcValid = false;
      } else if (
        updateTimesVisibleFields.includes("ats") &&
        ats &&
        !isNaN(ats.getTime()) &&
        atc &&
        ats.getTime() > atc.getTime()
      ) {
        setAtcError(
          t("form-validation.must-be-after", {
            subject: atcLabel,
            object: atsLabel,
          }),
        );
        isAtcValid = false;
      } else {
        setAtcError(null);
      }
    }

    // Validate FOAA fields if applicable
    if (isToFoaa) {
      const foaaValidation = foaaSchema.safeParse({
        latitude,
        longitude,
        vesselAnchoredShacklesInWater,
      });
      if (!foaaValidation.success) {
        const fieldErrors = foaaValidation.error.flatten().fieldErrors;
        if (fieldErrors.latitude?.[0]) errors.latitude = fieldErrors.latitude[0];
        if (fieldErrors.longitude?.[0]) errors.longitude = fieldErrors.longitude[0];
        if (fieldErrors.vesselAnchoredShacklesInWater?.[0]) {
          errors.vesselAnchoredShacklesInWater = fieldErrors.vesselAnchoredShacklesInWater[0];
        }
      }

      if (isSeaToAnchorage || isBerthToAnchorage || isAnchorageToAnchorage) {
        const locationValidation = locationSchema.safeParse(location);
        if (!locationValidation.success) {
          errors.location = locationValidation.error.errors[0]?.message;
        }
      }
    }

    if (isSeaToBerth) {
      const pilotOnBoard = t("vessel-call.movement-services.update-form.fields.pilot-on-board");
      const allFastLabel = t("vessel-call.movement-services.update-form.fields.all-fast");

      const pobValue = updatedTimes.pob || movementDetails?.pobTime;
      const allFastValue = updatedTimes.allFast || movementDetails?.allFastTime;
      if (!pobValue) {
        errors.pob = t("form-validation.required", { label: pilotOnBoard });
      }
      if (!allFastValue) {
        errors.allFast = t("form-validation.required", { label: allFastLabel });
      }
    } else if (isToBerth && !isAnchorageToBerth) {
      // Validate TO BERTH required fields
      const pobLabel = t("vessel-call.movement-services.update-form.fields.pob");
      const allFastLabel = t("vessel-call.movement-services.update-form.fields.all-fast");
      const allLinesClearLabel = t(
        "vessel-call.movement-services.update-form.fields.all-lines-clear",
      );

      const pobValue = updatedTimes.pob || (movementDetails as any)?.pobTime;
      const allFastValue = updatedTimes.allFast || (movementDetails as any)?.allFastTime;
      const allLinesClear =
        updatedTimes.allLinesClear || (movementDetails as any)?.allLinesClearTime;

      if (!pobValue) {
        errors.pob = t("form-validation.required", { label: pobLabel });
      }
      if (!allFastValue) {
        errors.allFast = t("form-validation.required", { label: allFastLabel });
      }

      // For FROM BERTH TO BERTH, also validate All Clear
      if (isBerthToBerth) {
        const allClearLabel = t("vessel-call.movement-services.update-form.fields.all-clear");
        const allClearValue = updatedTimes.allClear || (movementDetails as any)?.allClearTime;
        if (!allClearValue) {
          errors.allClear = t("form-validation.required", { label: allClearLabel });
        }
      } else {
        // For SEA/FOAA TO BERTH, validate All Lines Clear
        if (!allLinesClear) {
          errors.allLinesClear = t("form-validation.required", { label: allLinesClearLabel });
        }
      }
    }

    // Validate SEA required fields
    if (isToSea && !isBerthToSea && !isAnchorageToSea) {
      if (!permissionToSailTime) {
        errors.permissionToSailTime = t("form-validation.required", {
          label: permissionToSailLabel,
        });
      } else if (isPermissionToSailTimeChanged && isAfterCurrentMinute(permissionToSailTime)) {
        errors.permissionToSailTime = t("form-validation.must-not-be-in-future", {
          label: permissionToSailLabel,
        });
      }
    }

    const pilotageValidation = pilotageSchema.safeParse({
      pilotageType: pilotageType ?? undefined,
    });
    if (!pilotageValidation.success) {
      errors.pilotageType = t("form-validation.required", { label: pilotageTypeLabel });
    }

    if (pilotageType?.value === PILOTAGE_TYPE_OTHER) {
      const specifyIfOthersValidation = specifyIfOthersSchema.safeParse({ specifyIfOthers });
      if (!specifyIfOthersValidation.success) {
        errors.specifyIfOthers =
          specifyIfOthersValidation.error.flatten().fieldErrors.specifyIfOthers?.[0];
      }
    }

    // Return early only if there are any errors (ATC, FOAA, or TO BERTH)
    if (!isAtcValid || Object.keys(errors).length > 0) {
      setAdditionalErrors(errors);
      return;
    }

    // Validate resources
    const resourcesError = getResourcesValidationError(resources, t);
    if (resourcesError) {
      errors.resources = resourcesError;
    }

    // Set all errors and return if there are any (ATC error is set separately)
    if (Object.keys(errors).length > 0) {
      setAdditionalErrors(errors);
    } else {
      setAdditionalErrors({});
    }

    // Return early if there are any validation errors (ATC or other fields)
    if (!isAtcValid || Object.keys(errors).length > 0) {
      return;
    }

    type MovementDetailsAllLinesClear = MovementServiceDetailsBackendResponse & {
      allLinesClear?: string | null;
      allLinesClearTime?: string | null;
      allClearTime?: string | null;
    };
    const movementDetailsWithAllLinesClear = movementDetails as
      | MovementDetailsAllLinesClear
      | undefined;
    const allLinesClearSource =
      movementDetailsWithAllLinesClear?.allLinesClearTime ??
      movementDetailsWithAllLinesClear?.allLinesClear ??
      null;
    const allLinesClearTimeISO =
      updatedTimes.allLinesClear?.toISOString() ?? safeDateToISO(allLinesClearSource);

    // Build payload matching the API spec exactly
    const payload: CloseMovementServicePayload = {
      atc: atc!.toISOString(),
      // FOAA-specific fields
      ...(isToFoaa && {
        ...(latitude &&
          !isNaN(parseFloat(latitude)) && {
            latitude: parseFloat(latitude),
          }),
        ...(longitude &&
          !isNaN(parseFloat(longitude)) && {
            longitude: parseFloat(longitude),
          }),
        ...((isSeaToAnchorage || isBerthToAnchorage || isAnchorageToAnchorage) &&
          location && { location }),
        ...(vesselAnchoredShacklesInWater &&
          !isNaN(parseFloat(vesselAnchoredShacklesInWater)) && {
            vesselAnchoredShacklesInWater: parseFloat(vesselAnchoredShacklesInWater),
          }),
      }),
      // SEA specific fields
      ...(isToSea &&
        permissionToSailTime && {
          permissionToSailTime: permissionToSailTime.toISOString(),
        }),
      // Time fields - use updatedTimes if available, otherwise from movementDetails
      ...(!isSeaToBerth &&
        !isBerthToBerth &&
        (updatedTimes.anchorTime
          ? { anchorTime: updatedTimes.anchorTime.toISOString() }
          : safeDateToISO((movementDetails as any)?.anchorTime) && {
              anchorTime: safeDateToISO((movementDetails as any).anchorTime)!,
            })),
      ...(!isBerthToAnchorage &&
        !isSeaToBerth &&
        !isBerthToBerth &&
        (updatedTimes.anchorAweighTime
          ? { anchorAweighTime: updatedTimes.anchorAweighTime.toISOString() }
          : safeDateToISO((movementDetails as any)?.anchorAweighTime) && {
              anchorAweighTime: safeDateToISO((movementDetails as any).anchorAweighTime)!,
            })),
      ...(updatedTimes.pob
        ? { pobTime: updatedTimes.pob.toISOString() }
        : safeDateToISO((movementDetails as any)?.pobTime) && {
            pobTime: safeDateToISO((movementDetails as any).pobTime)!,
          }),
      ...((isSeaToBerth || isSeaToAnchorage || isAnchorageToBerth || isBerthToBerth) &&
        (updatedTimes.eta || safeDateToISO(etaSource)) && {
          eta: updatedTimes.eta?.toISOString() ?? safeDateToISO(etaSource)!,
        }),
      ...((isBerthToSea || isAnchorageToSea || isBerthToBerth || isBerthToAnchorage) &&
        (updatedTimes.etd || safeDateToISO(movementDetails?.etd)) && {
          etd: updatedTimes.etd?.toISOString() ?? safeDateToISO(movementDetails?.etd)!,
        }),
      ...((isSeaToBerth ||
        isBerthToSea ||
        isAnchorageToBerth ||
        isAnchorageToAnchorage ||
        isBerthToBerth ||
        isBerthToAnchorage) &&
        (updatedTimes.ets || safeDateToISO(movementDetails?.ets)) && {
          ets: updatedTimes.ets?.toISOString() ?? safeDateToISO(movementDetails?.ets)!,
        }),
      ...((isSeaToBerth ||
        isBerthToSea ||
        isAnchorageToBerth ||
        isBerthToBerth ||
        isBerthToAnchorage) &&
        (updatedTimes.etc || safeDateToISO(movementDetails?.etc)) && {
          etc: updatedTimes.etc?.toISOString() ?? safeDateToISO(movementDetails?.etc)!,
        }),
      ...((isSeaToBerth ||
        isBerthToSea ||
        isAnchorageToBerth ||
        isBerthToBerth ||
        isBerthToAnchorage) &&
        (updatedTimes.pilotUnderway || safeDateToISO(movementDetails?.pilotUnderwayTime)) && {
          pilotUnderwayTime:
            updatedTimes.pilotUnderway?.toISOString() ??
            safeDateToISO(movementDetails?.pilotUnderwayTime)!,
        }),
      ...((isSeaToBerth || isAnchorageToBerth || isBerthToBerth) &&
        (updatedTimes.firstLineAshore || safeDateToISO(movementDetails?.firstLineAshoreTime)) && {
          firstLineAshoreTime:
            updatedTimes.firstLineAshore?.toISOString() ??
            safeDateToISO(movementDetails?.firstLineAshoreTime)!,
        }),
      ...(!isBerthToAnchorage &&
        (updatedTimes.allFast
          ? { allFastTime: updatedTimes.allFast.toISOString() }
          : safeDateToISO((movementDetails as any)?.allFastTime) && {
              allFastTime: safeDateToISO((movementDetails as any).allFastTime)!,
            })),
      ...((isSeaToBerth ||
        isBerthToSea ||
        isAnchorageToBerth ||
        isBerthToBerth ||
        isBerthToAnchorage) &&
        (updatedTimes.pilotDisembark || safeDateToISO(movementDetails?.pilotDisembarkTime)) && {
          pilotDisembarkTime:
            updatedTimes.pilotDisembark?.toISOString() ??
            safeDateToISO(movementDetails?.pilotDisembarkTime)!,
        }),
      ...((isSeaToBerth ||
        isBerthToSea ||
        isAnchorageToSea ||
        isSeaToAnchorage ||
        isAnchorageToBerth ||
        isAnchorageToAnchorage ||
        isBerthToBerth ||
        isBerthToAnchorage) &&
        (updatedTimes.ats || safeDateToISO(atsSource)) && {
          ats: updatedTimes.ats?.toISOString() ?? safeDateToISO(atsSource)!,
        }),
      ...((isBerthToSea || isBerthToBerth || isBerthToAnchorage) &&
        (updatedTimes.allClear || safeDateToISO(movementDetails?.allClearTime)) && {
          allClearTime:
            updatedTimes.allClear?.toISOString() ?? safeDateToISO(movementDetails?.allClearTime)!,
        }),
      // @TODO To be decided if variable allClearTime should be used for other movements than BERTH to BERTH
      ...(!isBerthToAnchorage &&
        !isSeaToBerth &&
        !isBerthToBerth &&
        allLinesClearTimeISO && { allLinesClearTime: allLinesClearTimeISO }),
      ...(externalNotes && { externalNotes }),
      ...(externalAttachmentsUrl && {
        externalAttachments: [
          {
            url: externalAttachmentsUrl,
            type: "EXTERNAL_ATTACHMENT",
            originalFileName: externalAttachmentsFiles[0]?.name || "attachment",
          },
        ],
      }),
      // Internal notes (only if user has access)
      ...(hasInternalNotesAccess &&
        internalNotes && {
          internalNotes,
        }),
      // Internal attachments (only if user has access and file is uploaded)
      ...(hasInternalNotesAccess &&
        internalAttachmentsUrl && {
          internalAttachments: [
            {
              url: internalAttachmentsUrl,
              type: "INTERNAL_ATTACHMENT",
              originalFileName: internalAttachmentsFiles[0]?.name || "attachment",
            },
          ],
        }),
      ...(pilotageType && { pilotageType: String(pilotageType.value) }),
      ...(pilotageType?.value === PILOTAGE_TYPE_OTHER && {
        specifyIfOthers: specifyIfOthers.trim(),
      }),
      ...((updatedTimes.pilotStationCode || movementDetails?.pilotStationCode) && {
        pilotStationCode: updatedTimes.pilotStationCode ?? movementDetails?.pilotStationCode,
      }),
      // Additional resources
      ...(resources.length > 0 && {
        additionalResources: resources.map((r) => {
          const resourcePayload: {
            additionalResourceType: string;
            numberOfResources: number;
            startTimeOfUsage?: string;
            endTimeOfUsage?: string;
            resourceName?: string;
            connectionTime?: string;
            location?: string;
          } = {
            additionalResourceType: r.additionalResourceType,
            numberOfResources: r.numberOfResources,
          };

          if (r.startTimeOfUsage) {
            resourcePayload.startTimeOfUsage = r.startTimeOfUsage;
          }
          if (r.endTimeOfUsage) {
            resourcePayload.endTimeOfUsage = r.endTimeOfUsage;
          }
          if (r.resourceName && r.resourceName.trim()) {
            resourcePayload.resourceName = r.resourceName.trim();
          }
          if (r.connectionTime) {
            resourcePayload.connectionTime = r.connectionTime;
          }
          if (r.location && r.location.trim()) {
            resourcePayload.location = r.location.trim();
          }

          return resourcePayload;
        }),
      }),
    };

    // Make the API call to close the movement service
    await closeMovementService({
      id: movementServiceId!,
      service: serviceVesselCall,
      payload,
    });
  };

  const handleAddResource = (resource: Resource) => {
    const resourceWithId = { ...resource, id: `new-${Date.now()}` };
    setResources((prev) => {
      const nextResources = [...prev, resourceWithId];
      setAdditionalErrors((currentErrors) => ({
        ...currentErrors,
        resources: getResourcesValidationError(nextResources, t),
      }));
      return nextResources;
    });
    setIsAddResourceDrawerOpen(false);
  };

  const handleEditResource = (index: number, resource: Resource) => {
    setResources((prev) => {
      const updated = [...prev];
      updated[index] = resource;
      setAdditionalErrors((currentErrors) => ({
        ...currentErrors,
        resources: getResourcesValidationError(updated, t),
      }));
      return updated;
    });
    setIsEditResourceDrawerOpen(false);
    setEditingResourceIndex(null);
  };

  const handleRemoveResource = (index: number) => {
    setResources((prev) => {
      const nextResources = prev.filter((_, i) => i !== index);
      setAdditionalErrors((currentErrors) => ({
        ...currentErrors,
        resources: getResourcesValidationError(nextResources, t),
      }));
      return nextResources;
    });
    setIsEditResourceDrawerOpen(false);
    setEditingResourceIndex(null);
  };

  const handleOpenEditResource = (index: number) => {
    setEditingResourceIndex(index);
    setIsEditResourceDrawerOpen(true);
  };

  const handleOpenUpdateTimesDrawer = () => {
    setIsUpdateTimesDrawerOpen(true);
  };

  const handleUpdateTimesSave = (times: UpdatedTimes) => {
    setUpdatedTimes(times);
    const clearedTimeErrors = Object.fromEntries(
      updateTimesVisibleFields
        .filter((fieldName) => Boolean(times[fieldName]))
        .map((fieldName) => [fieldName, undefined]),
    ) as Partial<Record<UpdateTimesField, string>>;
    setAdditionalErrors((prev) => ({
      ...prev,
      ...clearedTimeErrors,
    }));
  };

  if (!currentMovement) {
    return null;
  }

  return (
    <>
      <div data-testid="close-movement-service-form">
        <SideDrawer
          isOpen={isOpen}
          title={t("vessel-call.movement-services.close-title")}
          onClose={handleCloseDrawer}
          submitLabel={t("vessel-call.movement-services.close-button")}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          width={650}
        >
          <div className="px-6 py-4 flex flex-col gap-6">
            <Typography
              variant="body1"
              data-testid="movement-service-form-title"
              sx={{ color: "text.primary" }}
            >
              {t("vessel-call.movement-services.close-subtitle")}
            </Typography>

            {/* ATC Field */}
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full">
                <CustomFormLabel data-testid="atc-label">{atcLabel}</CustomFormLabel>
                <DatePicker
                  quickCapture
                  hasError={Boolean(atcError)}
                  value={asDateOrNull(atc)}
                  onChange={(d) => {
                    setAtc(d);
                    setIsAtcChanged(true);
                    if (atcError) setAtcError(null);
                  }}
                  testId="atc-date-input"
                  combinedInput
                  dateConstraint="not-future"
                />
                {atcError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {atcError}
                  </Typography>
                )}
              </div>
            </div>

            {/* FOAA Fields */}
            {isToFoaa && (
              <>
                {/* Latitude and Longitude */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="w-full">
                      <CustomFormLabel data-testid="latitude-label">
                        {t("vessel-call.movement-services.close-form.fields.latitude")}
                      </CustomFormLabel>
                      <div className="max-sm:flex-col flex gap-4">
                        <Input
                          testId="latitude-input"
                          type="text"
                          slotProps={{
                            ...inputSlotProps,
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">25&#176;</InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment sx={{ marginLeft: "0" }} position="end">
                                  ' N
                                </InputAdornment>
                              ),
                            },
                            htmlInput: {
                              ...inputSlotProps.htmlInput,
                              inputMode: "decimal",
                              maxLength: 5,
                              pattern: "[0-9]{1,2}(\\.[0-9]{0,2})?",
                            },
                          }}
                          sx={coordinateInputSxProps}
                          value={latitude}
                          onChange={(e) => {
                            setLatitude(normalizeCoordinateMinute(e.target.value));
                            if (additionalErrors.latitude) {
                              setAdditionalErrors((prev) => ({ ...prev, latitude: undefined }));
                            }
                          }}
                          placeholder="value"
                        />
                      </div>
                      {additionalErrors.latitude && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {additionalErrors.latitude}
                        </Typography>
                      )}
                    </div>

                    <div className="w-full">
                      <CustomFormLabel data-testid="longitude-label">
                        {t("vessel-call.movement-services.close-form.fields.longitude")}
                      </CustomFormLabel>
                      <div className="max-sm:flex-col flex gap-4 ">
                        <Input
                          testId="longitude-input"
                          type="text"
                          slotProps={{
                            ...inputSlotProps,
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">056&#176;</InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment sx={{ marginLeft: "0" }} position="end">
                                  ' E
                                </InputAdornment>
                              ),
                            },
                            htmlInput: {
                              ...inputSlotProps.htmlInput,
                              inputMode: "decimal",
                              maxLength: 5,
                              pattern: "[0-9]{1,2}(\\.[0-9]{0,2})?",
                            },
                          }}
                          sx={coordinateInputSxProps}
                          value={longitude}
                          onChange={(e) => {
                            setLongitude(normalizeCoordinateMinute(e.target.value));
                            if (additionalErrors.longitude) {
                              setAdditionalErrors((prev) => ({ ...prev, longitude: undefined }));
                            }
                          }}
                          placeholder="value"
                        />
                      </div>
                      {additionalErrors.longitude && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {additionalErrors.longitude}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>

                {/* Location field */}
                {(isSeaToAnchorage || isBerthToAnchorage || isAnchorageToAnchorage) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="w-full">
                      <CustomFormLabel data-testid="location-label">
                        {t("vessel-call.movement-services.close-form.fields.location")}
                      </CustomFormLabel>
                      <Autocomplete
                        options={anchorageOptions}
                        value={anchorageOptions.find((option) => option.value === location) ?? null}
                        onChange={(option) => {
                          setLocation(option?.value != null ? String(option.value) : "");
                          if (additionalErrors.location) {
                            setAdditionalErrors((prev) => ({ ...prev, location: undefined }));
                          }
                        }}
                        testId="anchorage-location-autocomplete"
                        placeholder={t("vessel-call.movement-services.close-form.fields.location")}
                        error={Boolean(additionalErrors.location)}
                      />
                      {additionalErrors.location && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {additionalErrors.location}
                        </Typography>
                      )}
                    </div>
                  </div>
                )}

                {/* Vessel Anchored Shackles */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="w-full">
                    <CustomFormLabel data-testid="shackles-label">
                      {t(
                        "vessel-call.movement-services.close-form.fields.vessel-anchored-shackles",
                      )}
                    </CustomFormLabel>
                    <Input
                      type="number"
                      value={vesselAnchoredShacklesInWater}
                      decimals={0}
                      onChange={(e) => {
                        if (e.target.value.length > SHACKLES_MAX_DIGITS) {
                          setAdditionalErrors((prev) => ({
                            ...prev,
                            vesselAnchoredShacklesInWater: t("form-validation.max-length-digits", {
                              label: t(
                                "vessel-call.movement-services.close-form.fields.vessel-anchored-shackles",
                              ),
                              maxDigits: SHACKLES_MAX_DIGITS,
                            }),
                          }));
                          return;
                        }
                        setVesselAnchoredShacklesInWater(e.target.value);
                        if (additionalErrors.vesselAnchoredShacklesInWater) {
                          setAdditionalErrors((prev) => ({
                            ...prev,
                            vesselAnchoredShacklesInWater: undefined,
                          }));
                        }
                      }}
                      testId="shackles-input"
                      onBlur={() => {
                        const maxDigitsError = t("form-validation.max-length-digits", {
                          label: t(
                            "vessel-call.movement-services.close-form.fields.vessel-anchored-shackles",
                          ),
                          maxDigits: SHACKLES_MAX_DIGITS,
                        });
                        setAdditionalErrors((prev) =>
                          prev.vesselAnchoredShacklesInWater === maxDigitsError
                            ? { ...prev, vesselAnchoredShacklesInWater: undefined }
                            : prev,
                        );
                      }}
                      error={Boolean(additionalErrors.vesselAnchoredShacklesInWater)}
                      slotProps={inputSlotProps}
                      sx={alignedInputSxProps}
                      placeholder={t(
                        "vessel-call.movement-services.close-form.fields.vessel-anchored-shackles",
                      )}
                      fullWidth
                    />
                    {additionalErrors.vesselAnchoredShacklesInWater && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {additionalErrors.vesselAnchoredShacklesInWater}
                      </Typography>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* SEA Field */}
            {isToSea && !isBerthToSea && !isAnchorageToSea && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <CustomFormLabel data-testid="permission-to-sail-label">
                    {t("vessel-call.movement-services.close-form.fields.permission-to-sail")}
                  </CustomFormLabel>
                  <DatePicker
                    quickCapture
                    hasError={Boolean(additionalErrors.permissionToSailTime)}
                    value={asDateOrNull(permissionToSailTime)}
                    onChange={(value) => {
                      setPermissionToSailTime(value);
                      setIsPermissionToSailTimeChanged(true);
                      if (additionalErrors.permissionToSailTime) {
                        setAdditionalErrors((prev) => ({
                          ...prev,
                          permissionToSailTime: undefined,
                        }));
                      }
                    }}
                    testId="permission-to-sail-input"
                    combinedInput
                    dateConstraint="not-future"
                  />
                  {additionalErrors.permissionToSailTime && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {additionalErrors.permissionToSailTime}
                    </Typography>
                  )}
                </div>
              </div>
            )}

            {/* Times and Estimates Section */}
            <div className="pt-4">
              <Box
                sx={{
                  border: `1px solid ${theme.palette.strokeColor?.secondary || "#E6EDF2"}`,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                {/* Header with title and Update button */}
                <div className="flex justify-between items-center px-4 py-3">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t("vessel-call.movement-services.close-form.sections.times-and-estimates")}
                  </Typography>
                  <MuiButton
                    variant="text"
                    onClick={handleOpenUpdateTimesDrawer}
                    sx={{ textTransform: "none", color: "primary.main" }}
                  >
                    {t("vessel-call.movement-services.close-form.buttons.update")}
                  </MuiButton>
                </div>
                <div className="grid grid-cols-2" data-testid="close-times-and-estimates">
                  {(() => {
                    const allTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                      showFor?: "FOAA" | "BERTH" | "SEA";
                    }> = [
                      {
                        key: "anchorTime",
                        labelKey: "vessel-call.movement-services.update-form.fields.anchor-time",
                        value:
                          updatedTimes.anchorTime || (movementDetails as any)?.anchorTime || null,
                        showFor: "FOAA",
                      },
                      {
                        key: "anchorAweighTime",
                        labelKey: "vessel-call.movement-services.update-form.fields.anchor-aweigh",
                        value:
                          updatedTimes.anchorAweighTime ||
                          (movementDetails as any)?.anchorAweighTime ||
                          null,
                        showFor: "FOAA",
                      },
                      {
                        key: "pob",
                        labelKey: "vessel-call.movement-services.update-form.fields.pob",
                        value: updatedTimes.pob || (movementDetails as any)?.pobTime || null,
                        showFor: "BERTH",
                      },
                      {
                        key: "allFast",
                        labelKey: "vessel-call.movement-services.update-form.fields.all-fast",
                        value:
                          updatedTimes.allFast || (movementDetails as any)?.allFastTime || null,
                        showFor: "BERTH",
                      },
                      {
                        key: "allLinesClear",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.all-lines-clear",
                        value:
                          updatedTimes.allLinesClear ||
                          (movementDetails as any)?.allClearTime ||
                          null,
                        showFor: "BERTH" as const,
                      },
                    ];

                    // Times and Estimates section fields from the Close drawer
                    // The placeholder will preserve the layout
                    const seaToBerthTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "eta",
                        labelKey: "vessel-call.movement-services.update-form.fields.eta-readiness",
                        value: updatedTimes.eta || movementDetails?.eta || null,
                      },
                      {
                        key: "ets",
                        labelKey: "vessel-call.movement-services.update-form.fields.ets-readiness",
                        value: updatedTimes.ets || movementDetails?.ets || null,
                      },
                      {
                        key: "etc",
                        labelKey: "vessel-call.movement-services.update-form.fields.etc-completion",
                        value: updatedTimes.etc || movementDetails?.etc || null,
                      },
                      {
                        key: "pilotStationCode",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.pilot-boarding-area",
                        value:
                          updatedTimes.pilotStationCode ||
                          movementDetails?.pilotStationCode ||
                          null,
                      },
                      {
                        key: "pilotUnderway",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-underway",
                        value:
                          updatedTimes.pilotUnderway || movementDetails?.pilotUnderwayTime || null,
                      },
                      {
                        key: "pob",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-on-board",
                        value: updatedTimes.pob || movementDetails?.pobTime || null,
                      },
                      {
                        key: "firstLineAshore",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.first-line-ashore",
                        value:
                          updatedTimes.firstLineAshore ||
                          movementDetails?.firstLineAshoreTime ||
                          null,
                      },
                      {
                        key: "placeholder",
                        labelKey: "",
                        value: "",
                      },
                      {
                        key: "allFast",
                        labelKey: "vessel-call.movement-services.update-form.fields.all-fast",
                        value: updatedTimes.allFast || movementDetails?.allFastTime || null,
                      },
                      {
                        key: "pilotDisembark",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.pilot-disembarked",
                        value:
                          updatedTimes.pilotDisembark ||
                          movementDetails?.pilotDisembarkTime ||
                          null,
                      },
                      {
                        key: "ats",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.actual-time-start",
                        value: updatedTimes.ats || movementDetails?.ats || null,
                      },
                    ];

                    const berthToSeaTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "etd",
                        labelKey: "vessel-call.movement-services.update-form.fields.etd-readiness",
                        value: updatedTimes.etd || movementDetails?.etd || null,
                      },
                      {
                        key: "ets",
                        labelKey: "vessel-call.movement-services.update-form.fields.ets-readiness",
                        value: updatedTimes.ets || movementDetails?.ets || null,
                      },
                      {
                        key: "etc",
                        labelKey: "vessel-call.movement-services.update-form.fields.etc-completion",
                        value: updatedTimes.etc || movementDetails?.etc || null,
                      },
                      {
                        key: "etc-placeholder",
                        labelKey: "",
                        value: "",
                      },
                      {
                        key: "pilotUnderway",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-underway",
                        value:
                          updatedTimes.pilotUnderway || movementDetails?.pilotUnderwayTime || null,
                      },
                      {
                        key: "pob",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-on-board",
                        value: updatedTimes.pob || movementDetails?.pobTime || null,
                      },
                      {
                        key: "allClear",
                        labelKey: "vessel-call.movement-services.update-form.fields.all-clear",
                        value: updatedTimes.allClear || movementDetails?.allClearTime || null,
                      },
                      {
                        key: "pilotDisembark",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.pilot-disembarked",
                        value:
                          updatedTimes.pilotDisembark ||
                          movementDetails?.pilotDisembarkTime ||
                          null,
                      },
                      {
                        key: "ats",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.actual-time-start",
                        value: updatedTimes.ats || movementDetails?.ats || null,
                      },
                    ];

                    const anchorageToSeaTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "etd",
                        labelKey: "vessel-call.movement-services.update-form.fields.etd-readiness",
                        value: updatedTimes.etd || movementDetails?.etd || null,
                      },
                      {
                        key: "atc",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.ats-permission-to-sail",
                        value: updatedTimes.ats || movementDetails?.ats || null,
                      },
                    ];

                    const anchorageToBerthTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "eta",
                        labelKey: "vessel-call.movement-services.update-form.fields.eta-readiness",
                        value: updatedTimes.eta || movementDetails?.eta || null,
                      },
                      {
                        key: "ets",
                        labelKey: "vessel-call.movement-services.update-form.fields.ets-readiness",
                        value: updatedTimes.ets || movementDetails?.ets || null,
                      },
                      {
                        key: "etc",
                        labelKey: "vessel-call.movement-services.update-form.fields.etc-completion",
                        value: updatedTimes.etc || movementDetails?.etc || null,
                      },
                      {
                        key: "pilotStationCode",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.pilot-boarding-area",
                        value:
                          updatedTimes.pilotStationCode ||
                          movementDetails?.pilotStationCode ||
                          null,
                      },
                      {
                        key: "anchorAweighTime",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.anchor-aweigh-time",
                        value:
                          updatedTimes.anchorAweighTime ||
                          movementDetails?.anchorAweighTime ||
                          null,
                      },
                      {
                        key: "pilotUnderway",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-underway",
                        value:
                          updatedTimes.pilotUnderway || movementDetails?.pilotUnderwayTime || null,
                      },
                      {
                        key: "pob",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-on-board",
                        value: updatedTimes.pob || movementDetails?.pobTime || null,
                      },
                      {
                        key: "firstLineAshore",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.first-line-ashore",
                        value:
                          updatedTimes.firstLineAshore ||
                          movementDetails?.firstLineAshoreTime ||
                          null,
                      },
                      {
                        key: "allFast",
                        labelKey: "vessel-call.movement-services.update-form.fields.all-fast",
                        value: updatedTimes.allFast || movementDetails?.allFastTime || null,
                      },
                      {
                        key: "pilotDisembark",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.pilot-disembarked",
                        value:
                          updatedTimes.pilotDisembark ||
                          movementDetails?.pilotDisembarkTime ||
                          null,
                      },
                      {
                        key: "ats",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.actual-time-start",
                        value: updatedTimes.ats || movementDetails?.ats || null,
                      },
                    ];

                    const seaToAnchorageTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "eta",
                        labelKey: "vessel-call.movement-services.update-form.fields.eta-readiness",
                        value: updatedTimes.eta || etaSource || null,
                      },
                      {
                        key: "ats",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.ats-permission-to-anchor",
                        value: updatedTimes.ats || atsSource || null,
                      },
                    ];

                    const anchorageToAnchorageTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "ets",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.ets-permission-to-heave-anchor",
                        value: updatedTimes.ets || movementDetails?.ets || null,
                      },
                      {
                        key: "ats",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.ats-anchor-aweigh-underway",
                        value: updatedTimes.ats || movementDetails?.ats || null,
                      },
                    ];

                    const berthToBerthTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "etd",
                        labelKey: "vessel-call.movement-services.update-form.fields.etd-readiness",
                        value: updatedTimes.etd || movementDetails?.etd || null,
                      },
                      {
                        key: "eta",
                        labelKey: "vessel-call.movement-services.update-form.fields.eta-readiness",
                        value: updatedTimes.eta || movementDetails?.eta || null,
                      },
                      {
                        key: "ets",
                        labelKey: "vessel-call.movement-services.update-form.fields.ets-readiness",
                        value: updatedTimes.ets || movementDetails?.ets || null,
                      },
                      {
                        key: "etc",
                        labelKey: "vessel-call.movement-services.update-form.fields.etc-completion",
                        value: updatedTimes.etc || movementDetails?.etc || null,
                      },
                      {
                        key: "pilotUnderway",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-underway",
                        value:
                          updatedTimes.pilotUnderway || movementDetails?.pilotUnderwayTime || null,
                      },
                      {
                        key: "pilotUnderway-placeholder",
                        labelKey: "",
                        value: "",
                      },
                      {
                        key: "pob",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-on-board",
                        value: updatedTimes.pob || movementDetails?.pobTime || null,
                      },
                      {
                        key: "allClear",
                        labelKey: "vessel-call.movement-services.update-form.fields.all-clear",
                        value: updatedTimes.allClear || movementDetails?.allClearTime || null,
                      },
                      {
                        key: "firstLineAshore",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.first-line-ashore",
                        value:
                          updatedTimes.firstLineAshore ||
                          movementDetails?.firstLineAshoreTime ||
                          null,
                      },
                      {
                        key: "allFast",
                        labelKey: "vessel-call.movement-services.update-form.fields.all-fast",
                        value: updatedTimes.allFast || movementDetails?.allFastTime || null,
                      },
                      {
                        key: "pilotDisembark",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.pilot-disembarked",
                        value:
                          updatedTimes.pilotDisembark ||
                          movementDetails?.pilotDisembarkTime ||
                          null,
                      },
                      {
                        key: "ats",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.actual-time-start",
                        value: updatedTimes.ats || movementDetails?.ats || null,
                      },
                    ];

                    const berthToAnchorageTimeFields: Array<{
                      key: string;
                      labelKey: string;
                      value: string | Date | null | undefined;
                    }> = [
                      {
                        key: "etd",
                        labelKey: "vessel-call.movement-services.update-form.fields.etd-readiness",
                        value: updatedTimes.etd || movementDetails?.etd || null,
                      },
                      {
                        key: "ets",
                        labelKey: "vessel-call.movement-services.update-form.fields.ets-readiness",
                        value: updatedTimes.ets || movementDetails?.ets || null,
                      },
                      {
                        key: "pilotUnderway",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-underway",
                        value:
                          updatedTimes.pilotUnderway || movementDetails?.pilotUnderwayTime || null,
                      },
                      {
                        key: "pob",
                        labelKey: "vessel-call.movement-services.update-form.fields.pilot-on-board",
                        value: updatedTimes.pob || movementDetails?.pobTime || null,
                      },
                      {
                        key: "etc",
                        labelKey: "vessel-call.movement-services.update-form.fields.etc-completion",
                        value: updatedTimes.etc || movementDetails?.etc || null,
                      },
                      {
                        key: "etc-placeholder",
                        labelKey: "",
                        value: "",
                      },
                      {
                        key: "allClear",
                        labelKey: "vessel-call.movement-services.update-form.fields.all-clear",
                        value: updatedTimes.allClear || movementDetails?.allClearTime || null,
                      },
                      {
                        key: "pilotDisembark",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.pilot-disembarked",
                        value:
                          updatedTimes.pilotDisembark ||
                          movementDetails?.pilotDisembarkTime ||
                          null,
                      },
                      {
                        key: "anchorTime",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.vessel-anchor-time",
                        value: updatedTimes.anchorTime || movementDetails?.anchorTime || null,
                      },
                      {
                        key: "anchorTime-placeholder",
                        labelKey: "",
                        value: "",
                      },
                      {
                        key: "ats",
                        labelKey:
                          "vessel-call.movement-services.update-form.fields.actual-time-start",
                        value: updatedTimes.ats || movementDetails?.ats || null,
                      },
                    ];

                    // Filter fields based on destination
                    const timeFields = isBerthToBerth
                      ? berthToBerthTimeFields
                      : isBerthToAnchorage
                        ? berthToAnchorageTimeFields
                        : isBerthToSea
                          ? berthToSeaTimeFields
                          : isAnchorageToSea
                            ? anchorageToSeaTimeFields
                            : isSeaToBerth
                              ? seaToBerthTimeFields
                              : isAnchorageToBerth
                                ? anchorageToBerthTimeFields
                                : isSeaToAnchorage
                                  ? seaToAnchorageTimeFields
                                  : isAnchorageToAnchorage
                                    ? anchorageToAnchorageTimeFields
                                    : allTimeFields.filter((field) => {
                                        if (field.showFor === "FOAA") return isToFoaa;
                                        if (field.showFor === "BERTH") return isToBerth;
                                        if (field.showFor === "SEA") return isToSea;
                                        return false;
                                      });

                    if (timeFields.length === 0) {
                      return (
                        <div className="px-4 py-3">
                          <Typography variant="body2" color="text.secondary">
                            {t("vessel-call.movement-services.close-form.no-resources")}
                          </Typography>
                        </div>
                      );
                    }

                    return timeFields.map((field, index) => {
                      let formattedValue: string = "";
                      if (field.key === "pilotStationCode") {
                        formattedValue = (field.value as string) ?? "-";
                      } else {
                        formattedValue = safeFormatDate(field.value) ?? "-";
                      }
                      const rowIndex = Math.floor(index / 2);
                      const totalRows = Math.ceil(timeFields.length / 2);
                      const isLastRow = rowIndex === totalRows - 1;
                      const hasBottomBorder = !isLastRow;
                      const isRequiredField = updateTimesVisibleFields.includes(
                        field.key as UpdateTimesField,
                      );
                      const hasError =
                        isRequiredField &&
                        additionalErrors[field.key as keyof typeof additionalErrors];

                      return (
                        <div
                          key={field.key}
                          data-time-field={field.key}
                          className="flex h-full flex-col py-3"
                          style={{ paddingLeft: "16px", paddingRight: "16px" }}
                        >
                          <div className="flex items-center w-full">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                width: "50%",
                                fontSize: "14px",
                                fontWeight: 400,
                                marginRight: "16px",
                              }}
                            >
                              {t(field.labelKey)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                width: "50%",
                                minHeight: "20px",
                                fontSize: "14px",
                                fontWeight: 500,
                              }}
                            >
                              {formattedValue}
                            </Typography>
                          </div>
                          {hasError && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {hasError}
                            </Typography>
                          )}
                          {hasBottomBorder && (
                            <div
                              className="mt-auto border-b pt-3"
                              style={{
                                borderBottomColor: theme.palette.strokeColor?.secondary,
                                borderBottomWidth: "1px",
                              }}
                            />
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </Box>
            </div>

            {/* Resources Section */}
            <div className="pt-4">
              <div className="flex justify-between items-center mb-4">
                <Typography variant="subtitle1">
                  {t("vessel-call.movement-services.close-form.sections.resources")}
                </Typography>
                <MuiButton
                  variant="outlined"
                  onClick={() => setIsAddResourceDrawerOpen(true)}
                  sx={{ textTransform: "none" }}
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                >
                  {t("vessel-call.movement-services.close-form.buttons.add-resource")}
                </MuiButton>
              </div>

              {displayedResources.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("vessel-call.movement-services.close-form.no-resources")}
                </Typography>
              ) : (
                <div className="flex flex-col gap-4">
                  {displayedResources.map((resource, index) => (
                    <Box
                      key={resource.id || index}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: "background.paper",
                      }}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "16px" }}>
                          {t("vessel-call.movement-services.close-form.resource")} {index + 1}
                        </Typography>
                        <MuiButton
                          variant="text"
                          onClick={() => handleOpenEditResource(index)}
                          sx={{ textTransform: "none", color: "primary.main" }}
                        >
                          {t("vessel-call.movement-services.close-form.buttons.edit")}
                        </MuiButton>
                      </div>
                      {/* Resource details */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <div className="flex flex-col">
                          <Typography variant="body2" color="text.secondary">
                            {t("vessel-call.movement-services.close-form.fields.resource-type")}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                            {resource.additionalResourceType}
                          </Typography>
                        </div>
                        <div className="flex flex-col">
                          <Typography variant="body2" color="text.secondary">
                            {t("vessel-call.movement-services.close-form.fields.start-time")}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                            {safeFormatDate(resource.startTimeOfUsage) || "-"}
                          </Typography>
                        </div>
                        <div className="flex flex-col">
                          <Typography variant="body2" color="text.secondary">
                            {t("vessel-call.movement-services.close-form.fields.resource-name")}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                            {resource.resourceName || "-"}
                          </Typography>
                        </div>
                        <div className="flex flex-col">
                          <Typography variant="body2" color="text.secondary">
                            {t("vessel-call.movement-services.close-form.fields.end-time")}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                            {safeFormatDate(resource.endTimeOfUsage) || "-"}
                          </Typography>
                        </div>
                      </div>
                    </Box>
                  ))}
                </div>
              )}
              {additionalErrors.resources && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                  {additionalErrors.resources}
                </Typography>
              )}
            </div>

            <div className="pt-4 flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="w-full">
                  <CustomFormLabel data-testid="pilotage-type-label">
                    {pilotageTypeLabelText}
                  </CustomFormLabel>
                  <Autocomplete
                    options={pilotageTypeOptions}
                    value={pilotageType}
                    onChange={handlePilotageTypeChange}
                    testId="pilotage-type-autocomplete"
                    placeholder={pilotageTypeLabelText}
                    error={Boolean(additionalErrors.pilotageType)}
                  />
                  {additionalErrors.pilotageType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {additionalErrors.pilotageType}
                    </Typography>
                  )}
                </div>

                {isOtherPilotageType && (
                  <div className="w-full pt-4">
                    <CustomFormLabel data-testid="specify-if-others-label">
                      {specifyIfOthersLabelText}
                    </CustomFormLabel>
                    <Input
                      value={specifyIfOthers}
                      onChange={handleSpecifyIfOthersChange}
                      testId="specify-if-others-input"
                      slotProps={specifyIfOthersSlotProps}
                      sx={alignedInputSxProps}
                      placeholder={specifyIfOthersLabelText}
                      fullWidth
                    />
                    {additionalErrors.specifyIfOthers && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {additionalErrors.specifyIfOthers}
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-4 flex flex-col">
              <CustomFormLabel data-testid="external-notes-label">
                {t("vessel-call.movement-services.close-form.fields.notes-to-agent")}
              </CustomFormLabel>
              <Input
                value={externalNotes}
                onChange={(e) => setExternalNotes(e.target.value)}
                testId="external-notes-input"
                slotProps={notesSlotProps}
                sx={sxProps}
                placeholder={t("vessel-call.movement-services.close-form.fields.notes-to-agent")}
                multiline
                minRows={4}
                fullWidth
              />
              <FormHelperText sx={charCounterSx}>
                {externalNotes.length}/{NOTES_MAX_CHARS}
              </FormHelperText>
            </div>

            <div className="pt-4">
              <CustomFormLabel data-testid="external-attachments-label">
                {t("vessel-call.movement-services.close-form.fields.attachments")}
              </CustomFormLabel>
              <DocumentUpload
                testId="external-attachments-upload"
                label=""
                name="closeMovementExternalAttachments"
                isSideDrawer
                maxFiles={1}
                allowedExtensions={allowedSideDrawerExtensions}
                onUpload={(newFiles: File[]) => {
                  if (newFiles.length > 0) {
                    const file = newFiles[0];
                    setExternalAttachmentsFiles([file]);
                    triggerUploadUtil(
                      file,
                      handleExternalAttachmentsUpload,
                      externalAttachmentsUploadSignatureRef,
                    );
                  }
                }}
                onRemoveFile={() => {
                  setExternalAttachmentsFiles([]);
                  setExternalAttachmentsUrl(undefined);
                }}
                conditions={profile}
                files={externalAttachmentsFiles}
                setFiles={setExternalAttachmentsFiles}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
              {externalAttachmentsFiles[0] && (
                <div className="flex-1 mt-4">
                  <SingleFile
                    testId="external-attachments-upload"
                    file={externalAttachmentsFiles[0]}
                    fieldName="closeMovementExternalAttachments"
                    setFiles={() => {
                      setExternalAttachmentsFiles([]);
                      externalAttachmentsUploadSignatureRef.current = null;
                    }}
                  />
                </div>
              )}
            </div>

            {/* Internal Notes (only for Marine Department) */}
            {hasInternalNotesAccess && (
              <div className="pt-4 flex flex-col">
                <CustomFormLabel data-testid="internal-notes-label">
                  {t("vessel-call.movement-services.close-form.fields.internal-notes")}
                </CustomFormLabel>
                <Input
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  testId="internal-notes-input"
                  slotProps={notesSlotProps}
                  sx={sxProps}
                  placeholder={t("vessel-call.movement-services.close-form.fields.internal-notes")}
                  multiline
                  minRows={4}
                  fullWidth
                />
                <FormHelperText sx={charCounterSx}>
                  {internalNotes.length}/{NOTES_MAX_CHARS}
                </FormHelperText>
              </div>
            )}

            {/* Internal Attachments (only for Marine Department) */}
            {hasInternalNotesAccess && (
              <div className="pt-4">
                <CustomFormLabel data-testid="internal-attachments-label">
                  {t("vessel-call.movement-services.close-form.fields.internal-attachments")}
                </CustomFormLabel>
                <DocumentUpload
                  testId="internal-attachments-upload"
                  label=""
                  name="closeMovementInternalAttachments"
                  isSideDrawer
                  maxFiles={1}
                  allowedExtensions={allowedSideDrawerExtensions}
                  onUpload={(newFiles: File[]) => {
                    if (newFiles.length > 0) {
                      const file = newFiles[0];
                      setInternalAttachmentsFiles([file]);
                      triggerUploadUtil(
                        file,
                        handleInternalAttachmentsUpload,
                        internalAttachmentsUploadSignatureRef,
                      );
                    }
                  }}
                  onRemoveFile={() => {
                    setInternalAttachmentsFiles([]);
                    setInternalAttachmentsUrl(undefined);
                  }}
                  conditions={profile}
                  files={internalAttachmentsFiles}
                  setFiles={setInternalAttachmentsFiles}
                  uploadDescription={t("upload.dragOrDropDescription")}
                />
                {internalAttachmentsFiles[0] && (
                  <div className="flex-1 mt-4">
                    <SingleFile
                      testId="internal-attachments-upload"
                      file={internalAttachmentsFiles[0]}
                      fieldName="closeMovementInternalAttachments"
                      setFiles={() => {
                        setInternalAttachmentsFiles([]);
                        internalAttachmentsUploadSignatureRef.current = null;
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </SideDrawer>
      </div>

      {/* Add Resource Drawer */}
      <AddResourceDrawer
        isOpen={isAddResourceDrawerOpen}
        onClose={() => setIsAddResourceDrawerOpen(false)}
        onAdd={handleAddResource}
        resourceTypes={resourceTypes}
        resources={resourcesForAType}
        ats={atsForResourceValidation}
      />

      {/* Edit Resource Drawer */}
      {editingResourceIndex !== null && (
        <EditResourceDrawer
          isOpen={isEditResourceDrawerOpen}
          onClose={() => {
            setIsEditResourceDrawerOpen(false);
            setEditingResourceIndex(null);
          }}
          onSave={(resource) => handleEditResource(editingResourceIndex, resource)}
          onRemove={() => handleRemoveResource(editingResourceIndex)}
          resource={resources[editingResourceIndex]}
          resourceTypes={resourceTypes}
          resources={resourcesForAType}
          ats={atsForResourceValidation}
        />
      )}

      {/* Update Times Drawer */}
      <UpdateTimesDrawer
        isOpen={isUpdateTimesDrawerOpen}
        onClose={() => setIsUpdateTimesDrawerOpen(false)}
        visibleFields={[...updateTimesVisibleFields]}
        labelKeys={updateTimesConfig?.labelKeys}
        onSave={handleUpdateTimesSave}
        movementDetails={{
          eta: updatedTimes.eta ? updatedTimes.eta.toISOString() : etaSource || null,
          etd: updatedTimes.etd ? updatedTimes.etd.toISOString() : movementDetails?.etd || null,
          ets: updatedTimes.ets ? updatedTimes.ets.toISOString() : movementDetails?.ets || null,
          etc: updatedTimes.etc ? updatedTimes.etc.toISOString() : movementDetails?.etc || null,
          atc: safeDateToISO(atc) ?? null,
          pilotUnderway: updatedTimes.pilotUnderway
            ? updatedTimes.pilotUnderway.toISOString()
            : movementDetails?.pilotUnderwayTime || null,
          permissionToSail: updatedTimes.permissionToSail
            ? updatedTimes.permissionToSail.toISOString()
            : (movementDetails as any)?.ats || null,
          anchorTime: updatedTimes.anchorTime
            ? updatedTimes.anchorTime.toISOString()
            : (movementDetails as any)?.anchorTime || null,
          anchorAweighTime: updatedTimes.anchorAweighTime
            ? updatedTimes.anchorAweighTime.toISOString()
            : (movementDetails as any)?.anchorAweighTime || null,
          pob: updatedTimes.pob ? updatedTimes.pob.toISOString() : movementDetails?.pobTime || null,
          firstLineAshore: updatedTimes.firstLineAshore
            ? updatedTimes.firstLineAshore.toISOString()
            : movementDetails?.firstLineAshoreTime || null,
          allFast: updatedTimes.allFast
            ? updatedTimes.allFast.toISOString()
            : movementDetails?.allFastTime || null,
          pilotDisembark: updatedTimes.pilotDisembark
            ? updatedTimes.pilotDisembark.toISOString()
            : movementDetails?.pilotDisembarkTime || null,
          ats: updatedTimes.ats ? updatedTimes.ats.toISOString() : atsSource || null,
          allLinesClear: updatedTimes.allLinesClear
            ? updatedTimes.allLinesClear.toISOString()
            : movementDetails?.allLinesClearTime || null,
          allClear: updatedTimes.allClear
            ? updatedTimes.allClear.toISOString()
            : movementDetails?.allClearTime || null,
          pilotStationCode: updatedTimes.pilotStationCode
            ? updatedTimes.pilotStationCode
            : movementDetails?.pilotStationCode || null,
          movementType: movementDetails?.movementType || null,
          resources,
        }}
      />
    </>
  );
};

export default CloseMovementService;
